import { Cpu } from 'lucide-react';
import {
  formatCustomSandboxSizeSpec,
  type CustomSandboxSize,
  type ResourceSize,
} from '@runloop/reflex-contract';
import {
  DEFAULT_SANDBOX_RESOURCE_SIZE,
  DEFAULT_TRIAL_RESOURCE_SIZE,
  SANDBOX_RESOURCE_SIZE_OPTIONS,
  isResourceSizeAllowedForTrial,
  resolveOrgDefaultSandboxSize,
  resourceSizeCpuCores,
} from '@reflex/shared';
import {
  ConfigChipSelect,
  type ConfigChipSelectOption,
} from '@reflex/ui/components/agent/ConfigChipSelect';

const DEFAULT_SIZE = '__default__';
/**
 * Option-value prefix for org-defined custom sizes in the size picker, so
 * they share one string-valued select with the named `ResourceSize` tiers.
 */
const CUSTOM_SIZE_PREFIX = 'custom:';

/**
 * Chip icon for the devbox-size trigger. Pins the selected size's vCPU count as
 * a small badge in the CPU glyph's bottom-right corner — for the named tiers
 * (X-Large -> `4`) and custom sizes alike — so the chip conveys the box's scale
 * at a glance, not just its name. Falls back to a plain glyph when the count is
 * unknown.
 */
function DevboxSizeChipIcon({ cpuCores }: { cpuCores: number | null }) {
  if (cpuCores == null) {
    return <Cpu className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
  }
  return (
    <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center">
      <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
      <span
        aria-hidden
        className="absolute -bottom-1 -right-1 flex min-w-3 items-center justify-center rounded-full border border-border bg-background/50 px-[3px] text-[8px] font-medium leading-none text-foreground backdrop-blur-[1px]"
      >
        {formatCpuCoreBadge(cpuCores)}
      </span>
    </span>
  );
}

/** Compact vCPU count for the size-chip badge (e.g. `4`, `0.5`), no unit. */
function formatCpuCoreBadge(cpuCores: number): string {
  return Number.isInteger(cpuCores) ? String(cpuCores) : cpuCores.toFixed(1);
}

/**
 * Devbox resource-size picker rendered as a launch-style config chip. Offers
 * the named {@link ResourceSize} tiers, an org-default option, and the org's
 * own custom sizes under their own group. Shared by the agent launcher's
 * {@link AdvancedConfigChips} and the blueprint create form so both surfaces
 * offer identical sizing (including custom sizes) without drifting.
 */
export function DevboxSizeChip({
  value,
  onChange,
  customSize = null,
  onCustomSizeChange,
  customSizes = [],
  defaultSandboxSize = null,
  isTrial = false,
  ariaLabel = 'Devbox size',
  blueprintResourceSize = null,
  blueprintCustomSize = null,
  blueprintName = null,
}: {
  value: ResourceSize | null;
  onChange: (next: ResourceSize | null) => void;
  /** The currently selected org-defined custom size, if any. */
  customSize?: CustomSandboxSize | null;
  onCustomSizeChange?: (next: CustomSandboxSize | null) => void;
  /** The org's custom sizes, offered under their own group in the picker. */
  customSizes?: CustomSandboxSize[];
  /** The org's `defaultSandboxSize` setting; names the "(Default)" option. */
  defaultSandboxSize?: string | null;
  /** Whether the active Runloop account is subject to the trial size cap. */
  isTrial?: boolean;
  /** Accessible name for the chip trigger. Surfaces label the field per-context. */
  ariaLabel?: string;
  /**
   * Named size baked into the selected blueprint, if any. When the user hasn't
   * picked a size, the chip inherits it and labels the unset option as coming
   * "from &lt;blueprint&gt;" rather than the org/platform default.
   */
  blueprintResourceSize?: ResourceSize | null;
  /** Custom size baked into the selected blueprint (mutually exclusive with the named tier). */
  blueprintCustomSize?: CustomSandboxSize | null;
  /** Display name of the selected blueprint, shown in the "from &lt;blueprint&gt;" hint. */
  blueprintName?: string | null;
}) {
  const selected = SANDBOX_RESOURCE_SIZE_OPTIONS.find((o) => o.value === value) ?? null;
  // When the user hasn't picked a size, a size baked into the selected blueprint
  // wins over the org/platform default — "if a blueprint has a size, use it".
  // Trials are capped, so they keep the trial default rather than the (possibly
  // larger) blueprint size.
  const hasUserPick = value != null || customSize != null;
  const inheritsBlueprintSize =
    !isTrial && !hasUserPick && (blueprintResourceSize != null || blueprintCustomSize != null);
  const namedTierLabel = (size: ResourceSize) =>
    SANDBOX_RESOURCE_SIZE_OPTIONS.find((o) => o.value === size)?.label ?? size;
  // The unset option applies the org's configured default at launch time
  // (platform default when the org hasn't set one), so label it with the
  // size it resolves to — e.g. "Small (Default)" — instead of an opaque
  // "Default size".
  const orgDefault = resolveOrgDefaultSandboxSize(defaultSandboxSize, customSizes);
  const orgDefaultLabel = isTrial
    ? (SANDBOX_RESOURCE_SIZE_OPTIONS.find((o) => o.value === DEFAULT_TRIAL_RESOURCE_SIZE)?.label ??
      DEFAULT_TRIAL_RESOURCE_SIZE)
    : orgDefault?.kind === 'custom'
      ? orgDefault.size.name
      : namedTierLabel(orgDefault?.size ?? DEFAULT_SANDBOX_RESOURCE_SIZE);
  const defaultLabel = inheritsBlueprintSize
    ? blueprintCustomSize
      ? blueprintCustomSize.name
      : namedTierLabel(blueprintResourceSize!)
    : orgDefaultLabel;
  const defaultOptionLabel = inheritsBlueprintSize
    ? `${defaultLabel} · from ${blueprintName ?? 'blueprint'}`
    : `${defaultLabel} (Default)`;
  // vCPU count to badge on the trigger icon, mirroring `triggerLabel`: the
  // picked custom size, the picked named tier, or — when unset — whatever the
  // unset option resolves to (blueprint size, trial cap, org custom, org named,
  // or the platform default).
  const orgDefaultCpuCores = isTrial
    ? resourceSizeCpuCores(DEFAULT_TRIAL_RESOURCE_SIZE)
    : orgDefault?.kind === 'custom'
      ? orgDefault.size.cpuCores
      : resourceSizeCpuCores(orgDefault?.size ?? DEFAULT_SANDBOX_RESOURCE_SIZE);
  const defaultCpuCores = inheritsBlueprintSize
    ? (blueprintCustomSize?.cpuCores ?? resourceSizeCpuCores(blueprintResourceSize!))
    : orgDefaultCpuCores;
  const triggerCpuCores = customSize
    ? customSize.cpuCores
    : selected
      ? selected.cpuCores
      : defaultCpuCores;
  const encodedValue = customSize ? `${CUSTOM_SIZE_PREFIX}${customSize.name}` : value;
  const options: ConfigChipSelectOption<string | null>[] = [
    {
      key: DEFAULT_SIZE,
      value: null,
      label: <span className="text-muted-foreground">{defaultOptionLabel}</span>,
      searchValue: `default ${defaultLabel}`,
    },
    ...SANDBOX_RESOURCE_SIZE_OPTIONS.map((opt) => {
      const blocked = isTrial && !isResourceSizeAllowedForTrial(opt.value);
      return {
        key: opt.value,
        value: opt.value as string,
        label: blocked ? `${opt.label} (trial limit)` : opt.label,
        searchValue: `${opt.label} ${opt.value}`,
        trailing: (
          <span className="shrink-0 text-2xs text-muted-foreground">{opt.description}</span>
        ),
        group: '',
        disabled: blocked,
      };
    }),
    ...customSizes.map((size) => ({
      key: `${CUSTOM_SIZE_PREFIX}${size.name}`,
      value: `${CUSTOM_SIZE_PREFIX}${size.name}`,
      label: size.name,
      searchValue: `${size.name} custom`,
      trailing: (
        <span className="shrink-0 text-2xs text-muted-foreground">
          {formatCustomSandboxSizeSpec(size)}
        </span>
      ),
      group: 'Custom sizes',
      disabled: isTrial,
    })),
  ];
  return (
    <ConfigChipSelect
      ariaLabel={ariaLabel}
      triggerLabel={customSize ? customSize.name : selected ? selected.label : defaultOptionLabel}
      triggerIcon={<DevboxSizeChipIcon cpuCores={triggerCpuCores} />}
      options={options}
      value={encodedValue}
      onChange={(next) => {
        if (typeof next === 'string' && next.startsWith(CUSTOM_SIZE_PREFIX)) {
          const name = next.slice(CUSTOM_SIZE_PREFIX.length);
          onCustomSizeChange?.(customSizes.find((size) => size.name === name) ?? null);
          onChange(null);
          return;
        }
        onCustomSizeChange?.(null);
        onChange((next as ResourceSize | null) ?? null);
      }}
      description={
        isTrial
          ? 'Trial Runloop accounts are limited to Medium devboxes. Upgrade to unlock larger and custom sizes.'
          : undefined
      }
    />
  );
}
